import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PageTransitionService } from '../services/page-transition.service';

// Public pages between which a route change shows the full-screen page
// transition overlay (see PageTransitionService) instead of an instant swap.
const TRANSITION_ROUTES = new Set(['home', 'products', 'pricing', 'status', 'security', 'developer', 'contact', 'imprint', 'privacy', 'terms']);

function firstPathSegment(url: string): string {
  return url.split(/[?#]/)[0].replace(/^\//, '').split('/')[0];
}

/**
 * Delays activating the target route until the page-transition overlay is
 * fully faded in — so the page swap happens invisibly behind the overlay
 * instead of before it (the overlay is shown first, the actual navigation
 * only completes afterwards). Applies when navigating between two of
 * TRANSITION_ROUTES, and also on the very first navigation of the app
 * (landing on /home directly, e.g. typing the URL). Apply via
 * `canActivate: [pageTransitionGuard]` on each eligible route.
 */
export const pageTransitionGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const transition = inject(PageTransitionService);
  const to = firstPathSegment(state.url);

  // router.navigated is false until the app's first navigation completes —
  // there is no real "from" page yet, but landing on /home still counts as
  // "visiting the site".
  if (!router.navigated) {
    return to === 'home' ? transition.beginRouteTransition() : true;
  }

  // router.url still holds the outgoing route here — it only updates once
  // this navigation succeeds.
  const from = firstPathSegment(router.url);
  if (from === to || !TRANSITION_ROUTES.has(from) || !TRANSITION_ROUTES.has(to)) {
    return true;
  }

  return transition.beginRouteTransition();
};
